import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import useLang from '../hooks/useLang';

export default function Groups() {
  const { T } = useLang();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', code: '', account_number: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/groups').then((r) => setGroups(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.post('/groups', form);
      setShowModal(false);
      setForm({ name: '', code: '', account_number: '', description: '' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || T.groups.errCreate);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Futa kikundi "${name}"? Wanachama na miamala yote itafutwa.`)) return;
    await api.delete(`/groups/${id}`);
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">{T.groups.title}</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          {T.groups.addGroup}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500">{T.common.loading}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((g) => (
            <div key={g.id} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-start justify-between">
                <h3 className="font-bold text-gray-800 text-lg">{g.name}</h3>
                <button
                  onClick={() => handleDelete(g.id, g.name)}
                  className="text-red-400 hover:text-red-600 text-xs"
                >
                  {T.common.delete}
                </button>
              </div>
              {g.code && <p className="text-xs text-gray-400 mb-2">{T.groups.number}: {g.code}</p>}
              <div className="text-sm text-gray-600 mt-3 space-y-1">
                <div>{T.groups.members}: <span className="font-medium">{g.member_count}</span></div>
                <div>
                  {T.groups.total}:{' '}
                  <span className="font-medium text-green-700">
                    {new Intl.NumberFormat('sw-TZ').format(g.total_contributions)} TZS
                  </span>
                </div>
              </div>
              <Link
                to={`/groups/${g.id}`}
                className="mt-4 inline-block text-green-700 hover:text-green-900 text-sm font-medium"
              >
                {T.groups.viewDetail}
              </Link>
            </div>
          ))}
          {!groups.length && (
            <div className="col-span-3 text-center py-16 text-gray-400">
              {T.groups.noGroups}
            </div>
          )}
        </div>
      )}

      {/* Create modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-5">{T.groups.newGroup}</h2>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">
                {error}
              </div>
            )}

            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {T.groups.name} *
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="mfano: BLESSING FAMILY"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {T.groups.code}
                </label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {T.groups.accountNumber}
                </label>
                <input
                  type="text"
                  value={form.account_number}
                  onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                  placeholder="060243280129"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {T.groups.description}
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setError(''); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50"
                >
                  {T.common.cancel}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-green-700 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-green-800 disabled:opacity-60"
                >
                  {saving ? T.groups.creating : T.groups.create}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
